import { useEffect, useState } from "react";
import { API_BASE } from "../../apiBase";

const SavedScansTable = (props) => {
    const [savedScans, setSavedScans] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [nameRender, setNameRender] = useState(0);
    const { getAccessTokenSilently, user } = props.useAuth0();

    useEffect(() => {
        loadSavedScans();
    }, []);

    const loadSavedScans = async () => {
        setIsLoading(true);
        try {
            const accessToken = await getAccessTokenSilently();
            const response = await fetch(`${API_BASE}/live_search_result/${user.email}/all`, {
                method: 'GET',
                mode: "cors",
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                if (typeof data === 'object') {
                    const newData = Object.entries(data).map(([id, productCount]) => ({
                        id: Number(id),
                        productCount: productCount
                    }));
                    setSavedScans(newData);
                } else {
                    console.error("Unexpected data format:", data);
                    setSavedScans([]);
                }
            } else {
                console.error("Failed to load saved scans");
            }
        } catch (error) {
            console.error("Error loading saved scans:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return "Unknown date";
        const dateObj = String(timestamp).length === 10
            ? new Date(timestamp * 1000)
            : new Date(timestamp);

        const day = String(dateObj.getDate()).padStart(2, '');
        const monthNames = [
            "January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December"
        ];
        const month = monthNames[dateObj.getMonth()];
        const year = dateObj.getFullYear();
        const hours = String(dateObj.getHours()).padStart(2, '0');
        const minutes = String(dateObj.getMinutes()).padStart(2, '0');
        return `${day} ${month}, ${year} - ${hours}:${minutes}`;
    };

    const handleOpenScan = async (scanId) => {
        try {
            const accessToken = await getAccessTokenSilently();
            const response = await fetch(`${API_BASE}/live_search_result/${user.email}/${scanId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                props.setProducts(data.products)
                props.setFilters(data.filters)
            }
        } catch (error) {
            console.error("Error opening saved scan:", error);
        }
    };

    const handleDeleteScan = async (scanId) => {
        if (window.confirm("Are you sure you want to delete this scan?\nThis action cannot be undone.")) {
            setIsLoading(true);
            try {
                const accessToken = await getAccessTokenSilently();
                const response = await fetch(`${API_BASE}/live_search_result/`, {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${accessToken}`
                    },
                    body: JSON.stringify({
                        email: user.email,
                        id: scanId
                    })
                });

                if (response.ok) {
                    setSavedScans(savedScans.filter(scan => scan.id !== scanId));
                } else {
                    alert("Failed to delete scan\nPlease try again later.");
                }
            } catch (error) {
                alert("Failed to delete scan\nPlease try again later.");
            }
            finally {
                setIsLoading(false);
            }
        }
    };

    const containerStyle = {
        margin: "40px auto 60px",
        maxWidth: props.isMobile ? "95%" : "85%",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
        borderRadius: "8px",
        overflow: "hidden",
        backgroundColor: "#ffffff"
    };

    const headingStyle = {
        padding: "16px 24px",
        backgroundColor: "#f9f5fa",
        borderBottom: "1px solid #e8d8ea",
        margin: 0,
        fontWeight: 600,
        color: "#8B3C7E",
        fontSize: "1.25rem"
    };

    const tableHeaderStyle = {
        backgroundColor: "#f5e9f7",
        borderBottom: "2px solid #e3d0df"
    };

    const getRowStyle = (index) => ({
        backgroundColor: index % 2 === 0 ? "#ffffff" : "#faf6fb",
        borderBottom: "1px solid #f0e6f2",
        transition: "background-color 0.2s ease"
    });

    return (
        <div className="saved-scans-container" style={containerStyle}>
            <h3 style={headingStyle}>Saved Scans</h3>
            {isLoading ? (
                <div style={{ padding: "24px", textAlign: "center" }}>
                    <progress
                        className="progress is-small"
                        max="100"
                        style={{ maxWidth: "300px", margin: "0 auto" }}
                    >
                        15%
                    </progress>
                </div>
            ) : (
                <div className="table-responsive" style={{ overflowX: "auto" }}>
                    <table className="table is-fullwidth" style={{ margin: 0 }}>
                        <thead>
                            <tr style={tableHeaderStyle}>
                                <th style={{ padding: "12px 18px", color: "#8B3C7E", fontWeight: 600 }}>Name</th>
                                <th style={{ padding: "12px 18px", color: "#8B3C7E", fontWeight: 600 }}>Date</th>
                                <th style={{ padding: "12px 18px", color: "#8B3C7E", fontWeight: 600 }}>Products</th>
                                <th style={{ padding: "12px 18px", color: "#8B3C7E", fontWeight: 600, textAlign: "center" }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {savedScans.length === 0 ? (
                                <tr>
                                    <td colSpan="3" style={{
                                        textAlign: "center",
                                        padding: "24px",
                                        color: "#888"
                                    }}>
                                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                            <i
                                                className="fa fa-search"
                                                aria-hidden="true"
                                                style={{ fontSize: "2rem", marginBottom: "12px", opacity: 0.4 }}
                                            ></i>
                                            No saved scans found.
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                savedScans.map((scan, index) => (
                                    <tr
                                        key={scan.id}
                                        style={getRowStyle(index)}
                                        onMouseOver={(e) => {
                                            e.currentTarget.style.backgroundColor = "#f0e6f2";
                                        }}
                                        render={nameRender}
                                        onMouseOut={(e) => {
                                            e.currentTarget.style.backgroundColor = index % 2 === 0 ? "#ffffff" : "#faf6fb";
                                        }}
                                    >
                                        <td style={{ padding: "14px 18px", verticalAlign: "middle" }}>
                                            {localStorage.getItem(`savedLSearch${scan.id}Name`) ?
                                                <span style={{
                                                    display: "inline-block",
                                                    fontWeight: "600"
                                                }}>
                                                    {localStorage.getItem(`savedLSearch${scan.id}Name`)}
                                                    <i class="fa fa-pencil" onClick={() => { localStorage.removeItem(`savedLSearch${scan.id}Name`); setNameRender((nr) => nr + 1) }} aria-hidden="true" style={{ marginLeft: "12px", cursor: "pointer", textDecoration: "underline" }}></i>
                                                </span> :
                                                <>
                                                    <input placeholder={"Name the search"} style={{ marginTop: "8px" }} onBlur={(e) => { localStorage.setItem(`savedLSearch${scan.id}Name`, e.target.value); setNameRender((nr) => nr + 1) }} />
                                                    <i class="fa fa-check" aria-hidden="true" style={{ marginLeft: "12px", cursor: "pointer", textDecoration: "underline" }}></i>
                                                </>}
                                        </td>
                                        <td style={{ padding: "14px 18px", verticalAlign: "middle" }}>
                                            {formatDate(scan.id)}
                                        </td>
                                        <td style={{ padding: "14px 18px", verticalAlign: "middle" }}>
                                            <span style={{
                                                display: "inline-block",
                                                backgroundColor: "#8B3C7E15",
                                                padding: "4px 10px",
                                                borderRadius: "20px",
                                                fontSize: "0.95rem"
                                            }}>
                                                {scan.product_count || scan.productCount || "Unknown"} products
                                            </span>
                                        </td>
                                        <td style={{
                                            padding: "14px 18px",
                                            textAlign: "center",
                                            verticalAlign: "middle"
                                        }}>
                                            <div style={{
                                                display: "flex",
                                                justifyContent: "center",
                                                gap: "12px"  // Creates consistent spacing between buttons
                                            }}>
                                                <button
                                                    className="button is-small is-rounded"
                                                    onClick={() => handleOpenScan(scan.id)}
                                                    style={{
                                                        backgroundColor: "#8B3C7E",
                                                        color: "white",
                                                        boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
                                                        fontWeight: "600",
                                                        padding: "0 15px",
                                                        height: "2.2em",
                                                        transition: "all 0.2s ease",
                                                        border: "none",
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        minWidth: "90px"
                                                    }}
                                                    onMouseOver={(e) => {
                                                        e.currentTarget.style.backgroundColor = "#9D4C8E";
                                                        e.currentTarget.style.transform = "translateY(-1px)";
                                                        e.currentTarget.style.boxShadow = "0px 4px 6px rgba(0, 0, 0, 0.25)";
                                                    }}
                                                    onMouseOut={(e) => {
                                                        e.currentTarget.style.backgroundColor = "#8B3C7E";
                                                        e.currentTarget.style.transform = "translateY(0)";
                                                        e.currentTarget.style.boxShadow = "0px 2px 4px rgba(0, 0, 0, 0.2)";
                                                    }}
                                                >
                                                    <span style={{ marginRight: "5px" }}>Open</span>
                                                    <i className="fa fa-external-link" aria-hidden="true" style={{ fontSize: "0.85em" }}></i>
                                                </button>
                                                <button
                                                    className="button is-small is-rounded"
                                                    onClick={() => handleDeleteScan(scan.id)}
                                                    style={{
                                                        backgroundColor: "#ff3860",
                                                        color: "white",
                                                        boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
                                                        fontWeight: "600",
                                                        padding: "0 15px",
                                                        height: "2.2em",
                                                        transition: "all 0.2s ease",
                                                        border: "none",
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        minWidth: "90px"
                                                    }}
                                                    onMouseOver={(e) => {
                                                        e.currentTarget.style.backgroundColor = "#ff5b77";
                                                        e.currentTarget.style.transform = "translateY(-1px)";
                                                        e.currentTarget.style.boxShadow = "0px 4px 6px rgba(0, 0, 0, 0.25)";
                                                    }}
                                                    onMouseOut={(e) => {
                                                        e.currentTarget.style.backgroundColor = "#ff3860";
                                                        e.currentTarget.style.transform = "translateY(0)";
                                                        e.currentTarget.style.boxShadow = "0px 2px 4px rgba(0, 0, 0, 0.2)";
                                                    }}
                                                >
                                                    <span style={{ marginRight: "5px" }}>Delete</span>
                                                    <i className="fa fa-trash" aria-hidden="true" style={{ fontSize: "0.85em" }}></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default SavedScansTable